/**
 * Throw-safe Web Storage wrappers.
 *
 * Bare `localStorage` / `sessionStorage` access throws on storage-blocked
 * browsers — iOS Safari Private Browsing, "Block all cookies", or cross-origin
 * *partitioned* storage — surfacing as `SecurityError` or
 * `ReferenceError: Can't find variable: localStorage`. When such a throw escapes
 * render, an effect, or module init it crashes the page (worst on public routes
 * external users open). See #912, #974, #982.
 *
 * These helpers swallow every storage throw so callers degrade gracefully:
 *   - `get`    → returns the stored string, or `null` when storage is missing,
 *               blocked, or the key is unset (a blocked read is indistinguishable
 *               from "no stored value", which every existing caller already
 *               treats as the empty/default case).
 *   - `set`    → no-op when storage is missing, blocked, or full.
 *   - `remove` → no-op when storage is missing or blocked.
 *
 * This is the ONLY module allowed to touch `localStorage` / `sessionStorage`
 * directly — an ESLint `no-restricted-globals` rule flags bare access everywhere
 * else so this whole bug class cannot silently reappear.
 */

/* eslint-disable no-restricted-globals */

type StorageKind = "local" | "session";

/**
 * Resolve the underlying Storage without throwing.
 *
 * Merely *referencing* `localStorage` throws `ReferenceError` in some blocked
 * contexts, so the reference itself lives inside the try. Returns `null` when the
 * store is unavailable for any reason (SSR, private mode, blocked cookies).
 */
function getStore(kind: StorageKind): Storage | null {
  try {
    const store = kind === "local" ? localStorage : sessionStorage;
    // Some environments expose the global but leave it null/undefined.
    return store ?? null;
  } catch {
    return null;
  }
}

function makeSafeStorage(kind: StorageKind) {
  return {
    get(key: string): string | null {
      const store = getStore(kind);
      if (!store) {
        return null;
      }
      try {
        return store.getItem(key);
      } catch {
        return null;
      }
    },
    set(key: string, value: string): void {
      const store = getStore(kind);
      if (!store) {
        return;
      }
      try {
        store.setItem(key, value);
      } catch {
        // storage full / blocked — drop silently
      }
    },
    remove(key: string): void {
      const store = getStore(kind);
      if (!store) {
        return;
      }
      try {
        store.removeItem(key);
      } catch {
        // storage blocked — drop silently
      }
    },
  };
}

/* eslint-enable no-restricted-globals */

/** Throw-safe `localStorage` accessor. */
export const safeStorage = makeSafeStorage("local");

/** Throw-safe `sessionStorage` accessor. */
export const safeSession = makeSafeStorage("session");
